import { env } from "@/config/env";
import { readDevAuth } from "@/dev/devAuth";
import { isRecord } from "@/lib/typeGuards";

import { renewSession } from "./session";

/** Reasons a request can fail, independent of any HTTP status. */
export const API_ERROR_CODES = {
  /** Could not reach the API at all. */
  network: "api/network",
  /** HTTP status outside 2xx. */
  http: "api/http",
  /** 2xx, but the body does not match what the caller declared. */
  invalidResponse: "api/invalid-response",
  /** 2xx with `status: false` / non-success code — a business rejection. */
  business: "api/business",
  /** Still unauthorised after a token refresh. */
  unauthorised: "api/unauthorised",
} as const;

export type ApiErrorCode = (typeof API_ERROR_CODES)[keyof typeof API_ERROR_CODES];

export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly httpStatus: number | null;
  readonly businessCode: string | null;

  constructor(
    code: ApiErrorCode,
    message: string,
    httpStatus: number | null,
    businessCode: string | null = null,
  ) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.httpStatus = httpStatus;
    this.businessCode = businessCode;
  }
}

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

const UNAUTHORISED = 401;
const PIN_BUSINESS_CODE_PREFIX = "PIN_";

/** CustomerEndpoint / ResponseTransformInterceptor shape. */
const SUCCESS_RESPONSE_CODE = "0000";

/**
 * Normalized view of either wire envelope:
 * - new: `{ code, message, data }` (CustomerEndpoint)
 * - legacy: `{ status, message, payload }` (BaseResponseDto, e.g. /v1/auth/me)
 */
interface NormalizedEnvelope {
  ok: boolean;
  message: string;
  data: unknown;
  businessCode: string | null;
}

function normalizeEnvelope(value: unknown): NormalizedEnvelope | null {
  if (!isRecord(value) || typeof value["message"] !== "string") {
    return null;
  }

  if (typeof value["code"] === "string") {
    return {
      ok: value["code"] === SUCCESS_RESPONSE_CODE,
      message: value["message"],
      data: value["data"],
      businessCode: value["code"],
    };
  }

  if (typeof value["status"] === "boolean") {
    return {
      ok: value["status"] === true,
      message: value["message"],
      data: value["payload"],
      businessCode: null,
    };
  }

  return null;
}

async function send(
  method: HttpMethod,
  path: string,
  body: unknown,
): Promise<Response> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    // Second line of defence against CSRF: a cross-site form post cannot set a
    // custom header, so the API can reject anything without it.
    "X-SGC-Webview": "1",
  };

  // Development only, and only in the mode that asks for it: a browser tab has
  // no native shell to mint a code, so the token pasted into /dev/login stands
  // in. `readDevAuth` returns null in a production build.
  const devAuth = readDevAuth();
  if (devAuth !== null && devAuth.mode === "bearer") {
    headers["Authorization"] = `Bearer ${devAuth.token}`;
  }

  // The session is an HttpOnly cookie; the browser attaches it. Nothing in
  // this app can read it.
  const init: RequestInit = { method, headers, credentials: "include" };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(body);
  }

  try {
    return await fetch(`${env.apiBaseUrl}${path}`, init);
  } catch (error) {
    throw new ApiError(
      API_ERROR_CODES.network,
      error instanceof Error ? error.message : "Network request failed.",
      null,
    );
  }
}

/**
 * Calls the API and returns the envelope's payload (`data` or legacy
 * `payload`).
 *
 * A 401 means the web session expired: the shell is asked for a fresh one-time
 * code, that code buys a new session, and the request is retried once. A second
 * 401 is reported as `api/unauthorised` so the caller can hand the user back to
 * the shell.
 */
export async function apiRequest<T>(
  method: HttpMethod,
  path: string,
  body?: unknown,
): Promise<T> {
  let response = await send(method, path, body);
  const where = `${method} ${path}`;

  async function readBody(
    res: Response,
  ): Promise<{ json: unknown; envelope: NormalizedEnvelope | null } | null> {
    try {
      const parsed: unknown = await res.json();
      return { json: parsed, envelope: normalizeEnvelope(parsed) };
    } catch {
      return null;
    }
  }

  let parsedBody = await readBody(response);

  if (response.status === UNAUTHORISED) {
    // Some endpoints can reject a wrong PIN with HTTP 401 while still sending
    // an app envelope. Keep those as business rejections, but do not skip
    // session renewal for generic 401 envelopes like { code: "ERROR", ... }.
    const envelope401 = parsedBody?.envelope ?? null;
    if (
      envelope401 !== null &&
      typeof envelope401.businessCode === "string" &&
      envelope401.businessCode.startsWith(PIN_BUSINESS_CODE_PREFIX)
    ) {
      throw new ApiError(
        API_ERROR_CODES.business,
        envelope401.message,
        response.status,
        envelope401.businessCode,
      );
    }

    const renewed = await renewSession();
    if (renewed) {
      response = await send(method, path, body);
      parsedBody = await readBody(response);
    }
    if (response.status === UNAUTHORISED) {
      throw new ApiError(
        API_ERROR_CODES.unauthorised,
        "Session expired and could not be renewed.",
        UNAUTHORISED,
      );
    }
  }

  if (parsedBody === null) {
    throw new ApiError(
      response.ok ? API_ERROR_CODES.invalidResponse : API_ERROR_CODES.http,
      response.ok
        ? `${where} did not return JSON.`
        : `${where} failed with HTTP ${String(response.status)}.`,
      response.status,
    );
  }

  const { json, envelope } = parsedBody;

  if (!response.ok) {
    const apiMessage =
      envelope !== null && envelope.message.length > 0
        ? envelope.message
        : isRecord(json) &&
            typeof json["message"] === "string" &&
            json["message"].length > 0
          ? json["message"]
          : null;
    throw new ApiError(
      API_ERROR_CODES.http,
      apiMessage ?? `${where} failed with HTTP ${String(response.status)}.`,
      response.status,
      envelope?.businessCode ?? null,
    );
  }

  if (envelope === null) {
    throw new ApiError(
      API_ERROR_CODES.invalidResponse,
      `${where} did not return the standard response envelope.`,
      response.status,
    );
  }

  if (!envelope.ok) {
    throw new ApiError(
      API_ERROR_CODES.business,
      envelope.message,
      response.status,
      envelope.businessCode,
    );
  }

  return envelope.data as T;
}
